import { useState } from 'react';
import MapOutlined from '@mui/icons-material/MapOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleGlobalFilterButton,
  MRT_GlobalFilterTextField
} from 'material-react-table';
import TableDownloadButton from './TableDownloadButton';

import { getDefaultMRTOptions, TABLE_COLS, fetcher, aggregationCols, isArrayNonEmptySubset } from '../constants';
import useSWR from 'swr';
import { FormControl, InputLabel, NativeSelect, OutlinedInput, Snackbar } from '@mui/material';

const categoryAggregationMap = new Map([
  ['counties', 'County'],
  ['mpos', 'MPO'],
  ['districts', 'District'],
  ['regions', 'Region'],
  // ['State', 'State']
])

export default function RoadsTable(
  { data, mapRef, geoLayerRef, showOnMapData, setShowOnMapData, pagination, setPagination, category, year, layer }
) {

  const [aggregation, setAggregation] = useState('None')
  const [showAggErrorSnackbar, setShowAggErrorSnackbar] = useState(false)

  const shouldFetchAggregation = aggregation !== 'None' && category !== 'None' && layer !== 'None'
  const { data: aggData } = useSWR(
    shouldFetchAggregation
      ? `/aggregations?year=${year}&agg=${aggregation}&category=${categoryAggregationMap.get(category)}&layer=${layer}`
      : null,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      onError: () => {
        setShowAggErrorSnackbar(true)
        setAggregation('None')
      }
    }
  )

  // const { data: rowCount, error: rowCountError } = useSWR(`/stateCount?year=${year}`, fetcher, {
  //   revalidateIfStale: false,
  //   revalidateOnFocus: false,
  //   revalidateOnReconnect: false
  // })

  const table = useMaterialReactTable({
    ...getDefaultMRTOptions,
    columns: (aggregation !== 'None' && Array.isArray(aggData)) ? [aggregationCols[aggregation], ...TABLE_COLS.filter(c => Object.keys(aggData[0]).includes(c.id))] : TABLE_COLS,
    data: Array.isArray(aggData) ? aggData : (data ?? []),
    enableGrouping: !Array.isArray(aggData),
    enableRowActions: aggregation === 'None',
    enableColumnFilters: aggregation === 'None',
    state: {
      isLoading: shouldFetchAggregation && aggData === undefined,
    },
    renderTopToolbar: ({table}) => (
      <div style={{paddingTop: 15, paddingBottom: 15, display: 'flex', alignItems: 'center', height: '7vh'}}>
        { aggregation === 'None' && <MRT_ToggleFiltersButton table={table} /> }
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <TableDownloadButton table={table} category={category} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_GlobalFilterTextField table={table} />
        <FormControl disabled={data === null} sx={{marginTop: 1}}>
          <InputLabel htmlFor="select-aggregation">
            Aggregate By:
          </InputLabel>
          <NativeSelect
            className='roads-selection'
            input={<OutlinedInput label='select-aggregation' />}
            value={aggregation}
            onChange={(e) => {
              setAggregation(e.target.value)
              table.setColumnOrder((prev) => [e.target.value, ...prev])
            }}
            inputProps={{
              name: 'aggregation',
              id: 'select-aggregation'
            }}
          >
            <option value={'None'}>None</option>
            <option value={'freight'}>Freight</option>
            <option value={'urban'}>Urban/Rural</option>
            <option value={'fc'}>Functional Classification</option>
            <option value={'nhs'}>National Highway System</option>
          </NativeSelect>
        </FormControl>
      </div>
    ),
    muiTablePaperProps: {
      id: 'ILMAT_Summary'
    },
    muiTableHeadRowProps: ({}) => ({
      sx: (theme) => ({
        '& th': {
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderRight: `1px solid ${theme.palette.divider}`, 
        },
      }),
    }),
    muiTableBodyRowProps: ({row}) => ({
      sx: (theme) => ({
        '& td': {
          color: showOnMapData.includes(row.original.id) ? 'red' : theme.palette.text.primary,
          fontWeight: showOnMapData.includes(row.original.id) ? 'bold' : 'normal',
          borderLeft: '1px solid ' + theme.palette.divider,
          borderRight: '1px solid ' + theme.palette.divider,
        }
      })
    }),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        AggregatedCell: ({row}) => (
          <Tooltip title={isArrayNonEmptySubset(showOnMapData, row.subRows.map(d => d.original.id)) ? 'Undo' : 'Show on map'} placement='right'>
            <IconButton
              color='inherit'
              onClick={() => {
                const subRowIDs = row.subRows.map(d => d.original.id)
                if (isArrayNonEmptySubset(showOnMapData, subRowIDs)) {
                  setShowOnMapData(prev => prev.filter(p => !subRowIDs.includes(p)))
                } else {
                  setShowOnMapData(prev => [...prev, ...subRowIDs])
                }
              }}
            >
              <MapOutlined />
            </IconButton>
          </Tooltip>
        )
      }
    },
    renderRowActions: ({row}) => (
        <Tooltip title={showOnMapData.includes(row.original.id) ? 'Undo' : 'Show on map'} placement='right'>
          <IconButton
            color='inherit'
            onClick={() => {
              if (showOnMapData.includes(row.original.id)) {
                setShowOnMapData(prev => prev.filter(p => p !== row.original.id))
              } else {
                setShowOnMapData(prev => [...prev, row.original.id])
              }
            }}
          >
            <MapOutlined />
          </IconButton>
        </Tooltip>
      )
  });

  return (
  <>
    <Snackbar
      open={showAggErrorSnackbar}
      autoHideDuration={6000}
      onClose={(e, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setShowAggErrorSnackbar(false);
      }}
      message='Aggregation error. Please try again later.'
    />
    <MaterialReactTable table={table} />
  </>
  );

}